import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  Col,
  ConfigProvider,
  Divider,
  Dropdown,
  Menu,
  Row,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { changeLanguage, layoutDirection, selectCurrentDirection } from '../../features/ui/uiSlice';
import SocialAuth from './SocialAuth';
import { useSocialAuthMutation } from '../../features/auth/authService';
import { IResolveParams } from 'reactjs-social-login';
import { userLogOut } from '../../features/auth/authSlice';
import { useAuth } from '../../hooks/useAuth';
import { useCookies } from 'react-cookie';

type AuthLayoutType = {
  children: React.ReactNode;
};

const localeMapper = (locale: string) => {
  switch (locale) {
    case 'en':
      return 'English';
    case 'fr':
      return 'Français';
    case 'cn':
      return '中文';
    case 'es':
      return 'Español';
    case 'pt':
      return 'Português';
    case 'fa':
      return 'فارسی';
    case 'sa':
      return 'العربیه';
    default:
      return 'English';
  }
};

const Layout = ({ children }: AuthLayoutType) => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { is_authenticated, is_verified_payment_account, is_account_active } = useAuth();

  const layoutDirectionNow = useAppSelector(selectCurrentDirection);

  const [currentLanguage, setCurrentLanguage] = useState('');

  const [socialAuthMutation, { isLoading: isLoadingSocial }] = useSocialAuthMutation();

  const [cookies] = useCookies(['utm_datas', '_fbp', '_fbc']);

  const localeChanger = (locale: string, label: string) => {
    i18n.changeLanguage(locale);

    dispatch(layoutDirection(locale));

    dispatch(changeLanguage(locale));

    setCurrentLanguage(label);
  };

  const menu = (
    <Menu>
      <Menu.Item key="translation:1" onClick={() => localeChanger('en', 'English')}>
        <Space>
          <img src="https://flagcdn.com/gb.svg" width="30" alt="England" />
          English
        </Space>
      </Menu.Item>
      <Menu.Item key="translation:2" onClick={() => localeChanger('fr', 'Français')}>
        <Space>
          <img src="https://flagcdn.com/fr.svg" width="30" alt="France" />
          Français
        </Space>
      </Menu.Item>
      <Menu.Item key="translation:3" onClick={() => localeChanger('cn', '中文')}>
        <Space>
          <img src="https://flagcdn.com/cn.svg" width="30" alt="Mandarin" />
          中文
        </Space>
      </Menu.Item>
      <Menu.Item key="translation:4" onClick={() => localeChanger('es', 'Español')}>
        <Space>
          <img src="https://flagcdn.com/es.svg" width="30" alt="Spain" />
          Español
        </Space>
      </Menu.Item>
      <Menu.Item key="translation:7" onClick={() => localeChanger('pt', 'Português')}>
        <Space>
          <img src="https://flagcdn.com/pt.svg" width="30" alt="Portuguese" />
          Português
        </Space>
      </Menu.Item>
      {/* <Menu.Item key="translation:8" onClick={() => localeChanger('fa', 'فارسی')}>
        <Space>
          <img src="https://flagcdn.com/ir.svg" width="30" alt="Iran" />
          فارسی
        </Space>
      </Menu.Item>
      <Menu.Item key="translation:6" onClick={() => localeChanger('sa', 'العربية')}>
        <Space>
          <img src="https://flagcdn.com/sa.svg" width="30" alt="Saudi Arabia" />
          العربية
        </Space>
      </Menu.Item> */}
    </Menu>
  );

  const handleLoginStartSocialAuth = () => {};

  const handleResolveSocialAuth = (provider: IResolveParams['provider'], data: IResolveParams['data']) => {
    const cpPayload = { ...data, provider };

    let newData: any = { ...{ type: 'login', providerPayload: cpPayload } };

    if (window.navigator.cookieEnabled && cookies.utm_datas && cookies.utm_datas !== '') {
      newData.user_utm_data = cookies.utm_datas;

      newData.user_utm_data = {
        ...newData.user_utm_data,
        client_user_agent: navigator.userAgent,
      };

      if (cookies.utm_datas.fbclid) {
        if (cookies._fbp && cookies._fbp !== '') {
          newData.user_utm_data = {
            ...newData.user_utm_data,
            fbp: cookies._fbp,
          };
        }

        if (cookies._fbc && cookies._fbc !== '') {
          newData.user_utm_data = {
            ...newData.user_utm_data,
            fbc: cookies._fbc,
          };
        }
      }
    }

    socialAuthMutation(newData)
      .unwrap()
      .then(async (payload) => {
        navigate('/', { replace: true });
      })
      .catch((err: any) => {
        message.error(err.data.message || err.message);
      });
  };

  const handleRejectSocialAuth = (err: any) => {
    message.error('Unexpected error happened. Try again later.');
  };

  useEffect(() => {
    setCurrentLanguage(localeMapper(i18n.language));

    if (is_authenticated && !is_account_active) {
      navigate('/verify-account', { replace: true });

      return;
    }

    if (is_authenticated && !is_verified_payment_account) {
      navigate('/account-subscription', { replace: true });

      return;
    }

    if (is_authenticated && is_account_active && is_verified_payment_account) {
      navigate('/', { replace: true });

      return;
    }
  }, [i18n.language, navigate, is_authenticated, is_account_active, is_verified_payment_account]);

  return (
    <ConfigProvider direction={layoutDirectionNow}>
      <div className="form-container">
        <Row justify="center" style={{ margin: '3rem 0' }}>
          <Col xs={22} sm={18} md={16} lg={14} xl={12}>
            <Card>
              {is_authenticated && (
                <div className="logout-btn">
                  <Button ghost={true} type="primary" onClick={() => dispatch(userLogOut())}>
                    {t('App.Logout')}
                  </Button>
                </div>
              )}

              <div className="language-dropdown" style={{ zIndex: 99 }}>
                <Dropdown overlay={menu}>
                  <Space align="center">
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 512 512"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill="none"
                        strokeMiterlimit="10"
                        strokeWidth="32"
                        d="M464 256c0-114.87-93.13-208-208-208S48 141.13 48 256s93.13 208 208 208 208-93.13 208-208z"
                      ></path>
                      <path d="M445.57 172.14c-16.06.1-14.48 29.73-34.49 15.75-7.43-5.18-12-12.71-21.33-15-8.15-2-16.5.08-24.55 1.47-9.15 1.58-20 2.29-26.94 9.22-6.71 6.67-10.26 15.62-17.4 22.33-13.81 13-19.64 27.19-10.7 45.57 8.6 17.67 26.59 27.26 46 26 19.07-1.27 38.88-12.33 38.33 15.38-.2 9.8 1.85 16.6 4.86 25.71 2.79 8.4 2.6 16.54 3.24 25.21 1.18 16.2 4.16 34.36 12.2 48.67l15-21.16c1.85-2.62 5.72-6.29 6.64-9.38 1.63-5.47-1.58-14.87-1.95-21s-.19-12.34-1.13-18.47c-1.32-8.59-6.4-16.64-7.1-25.13-1.29-15.81 1.6-28.43-10.58-41.65-11.76-12.75-29-15.81-45.47-13.22-8.3 1.3-41.71 6.64-28.3-12.33 2.65-3.73 7.28-6.79 10.26-10.34 2.59-3.09 4.84-8.77 7.88-11.18s17-5.18 21-3.95 8.17 7 11.64 9.56a49.89 49.89 0 0021.81 9.36c13.66 2 42.22-5.94 42-23.46-.04-8.4-7.84-20.1-10.92-27.96zM287.45 316.3c-5.33-22.44-35.82-29.94-52.26-42.11-9.45-7-17.86-17.81-30.27-18.69-5.72-.41-10.51.83-16.18-.64-5.2-1.34-9.28-4.14-14.82-3.41-10.35 1.36-16.88 12.42-28 10.92-10.55-1.42-21.42-13.76-23.82-23.81-3.08-12.92 7.14-17.11 18.09-18.26 4.57-.48 9.7-1 14.09.67 5.78 2.15 8.51 7.81 13.7 10.67 9.73 5.33 11.7-3.19 10.21-11.83-2.23-12.94-4.83-18.22 6.71-27.12 8-6.14 14.84-10.58 13.56-21.61-.76-6.48-4.31-9.41-1-15.86 2.51-4.91 9.4-9.34 13.89-12.27 11.59-7.56 49.65-7 34.1-28.16-4.57-6.21-13-17.31-21-18.83-10-1.89-14.44 9.27-21.41 14.19-7.2 5.09-21.22 10.87-28.43 3-9.7-10.59 6.43-14.07 10-21.46s-8.27-21.36-14.61-24.9l-29.81 33.43a41.52 41.52 0 008.34 31.86c5.93 7.63 15.37 10.08 15.8 20.5.42 10-1.14 15.12-7.68 22.15-2.83 3-4.83 7.26-7.71 10.07-3.53 3.43-2.22 2.38-7.73 3.32-10.36 1.75-19.18 4.45-29.19 7.21C95.34 199.94 93.8 172.69 86.2 162l-25 20.19c-.27 3.31 4.1 9.4 5.29 13 6.83 20.57 20.61 36.48 29.51 56.16 9.37 20.84 34.53 15.06 45.64 33.32 9.86 16.2-.67 36.71 6.71 53.67 5.36 12.31 18 15 26.72 24 8.91 9.09 8.72 21.53 10.08 33.36a305.22 305.22 0 007.45 41.28c1.21 4.69 2.32 10.89 5.53 14.76 2.2 2.66 9.75 4.95 6.7 5.83 4.26.7 11.85 4.68 15.4 1.76 4.68-3.84 3.43-15.66 4.24-21 2.43-15.9 10.39-31.45 21.13-43.35 10.61-11.74 25.15-19.69 34.11-33 8.73-12.98 11.36-30.49 7.74-45.68zm-33.39 26.32c-6 10.71-19.36 17.88-27.95 26.39-2.33 2.31-7.29 10.31-10.21 8.58-2.09-1.24-2.8-11.62-3.57-14a61.17 61.17 0 00-21.71-29.95c-3.13-2.37-10.89-5.45-12.68-8.7-2-3.53-.2-11.86-.13-15.7.11-5.6-2.44-14.91-1.06-20 1.6-5.87-1.48-2.33 3.77-3.49 2.77-.62 14.21 1.39 17.66 2.11 5.48 1.14 8.5 4.55 12.82 8 11.36 9.11 23.87 16.16 36.6 23.14 9.86 5.46 12.76 12.37 6.46 23.62zm-69.6-275.53c4.74 4.63 9.2 10.11 16.27 10.57 6.69.45 13-3.17 18.84 1.38 6.48 5 11.15 11.33 19.75 12.89 8.32 1.51 17.13-3.35 19.19-11.86 2-8.11-2.31-16.93-2.57-25.07 0-1.13.61-6.15-.17-7-.58-.64-5.42.08-6.16.1q-8.13.24-16.22 1.12a207.1 207.1 0 00-57.18 14.65c2.43 1.68 5.48 2.35 8.25 3.22zm171.94 56.18c8.49 0 17.11-3.8 14.37-13.62-2.3-8.23-6.22-17.16-15.76-12.72-6.07 2.82-14.67 10-15.38 17.12-.81 8.08 11.11 9.22 16.77 9.22zm-6.78 42.97c8.67 5.19 21.53 2.75 28.07-4.66 5.11-5.8 8.12-15.87 17.31-15.86a15.4 15.4 0 0110.82 4.41c3.8 3.93 3.05 7.62 3.86 12.54 1.81 11.05 13.66.63 16.75-3.65 2-2.79 4.71-6.93 3.8-10.56-.84-3.39-4.8-7-6.56-10.11-5.14-9-9.37-19.47-17.07-26.74-7.41-7-16.52-6.19-23.55 1.08-5.76 6-12.45 10.75-16.39 18.05-2.78 5.13-5.91 7.58-11.54 8.91-3.1.73-6.64 1-9.24 3.08-7.24 5.7-3.12 19.39 3.74 23.51z"></path>
                    </svg>
                    {currentLanguage}
                  </Space>
                </Dropdown>
              </div>
              <div className="logo">
                <a href={process.env.REACT_APP_LUMBERJACK_URL}>
                  <Space direction="vertical" align="center">
                    <Avatar size={86} src="https://cdn.1cdn.app/application/LUMBERJACK/2208240622_logo.svg" />
                    <Typography.Title level={4} style={{ color: '#444', marginBottom: 0 }}>
                      <span>L</span>umberjac<span>k</span>
                    </Typography.Title>
                  </Space>
                </a>
              </div>

              {(location.pathname.includes('login') || location.pathname.includes('register')) && (
                <div className="loginRegisterTab">
                  <ul>
                    <li className={location.pathname.includes('register') ? 'active' : ''}>
                      <Link to={'/register'}>{t('Login.Sign up')}</Link>
                    </li>
                    <li className={location.pathname.includes('login') ? 'active' : ''}>
                      <Link to={'/login'}>{t('Login.Log in')}</Link>
                    </li>
                  </ul>

                  <SocialAuth
                    className="social-auth-container"
                    providers={['Google']}
                    onLoginStartSocialAuthStart={handleLoginStartSocialAuth}
                    onResolveSocialAuth={handleResolveSocialAuth}
                    onRejectSocialAuth={handleRejectSocialAuth}
                    providersIcon={{
                      Google: (
                        <div className="s-icon">
                          <button>
                            <img
                              src="https://cdn.1cdn.app/application/PIL/23022812192827_Google_-_Original.svg"
                              alt="Google social auth"
                            />
                            {isLoadingSocial ? <Spin /> : t('App.Continue with Google')}
                          </button>
                        </div>
                      ),
                    }}
                    rest={{ style: { marginTop: 20, justifyContent: 'center' } }}
                  />

                  <Divider style={{ color: '#C9C9C9' }}>{t('Login.Or')}</Divider>
                </div>
              )}

              <div className="content-wrapper">{children}</div>
            </Card>
          </Col>
        </Row>
      </div>
    </ConfigProvider>
  );
};

export default Layout;
